/**
 * venueHeaderFlagTexture.js — Adapted for RaceProX
 *
 * Flag textures now come from flagcdn URLs (e.g. https://flagcdn.com/w320/gb.png)
 * instead of local /textures/flags/ files.
 *
 * The API is unchanged from the source — existing consumers continue to work.
 */
import * as THREE from 'three';

export const VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT =
  'venue-header-flag-texture-changed';
export const DEFAULT_VENUE_HEADER_FLAG_URL = '/textures/checkered-flag.png';
export const DEFAULT_VENUE_HEADER_FLAG_LABEL = 'Default (checkered)';

const loader = new THREE.TextureLoader();
loader.crossOrigin = 'anonymous'; // Enable cross-origin for flagcdn URLs

/** @type {{ dataUrl: string | null; presetUrl: string | null; fileLabel: string; texture: THREE.Texture | null; isDefault: boolean }} */
let flagState = {
  dataUrl: null,
  presetUrl: null,
  fileLabel: DEFAULT_VENUE_HEADER_FLAG_LABEL,
  texture: null,
  isDefault: true,
};

/**
 * @param {THREE.Texture} tex
 */
function configureFlagTexture(tex) {
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  return tex;
}

function disposeActiveTexture() {
  if (flagState.texture && !flagState.isDefault) {
    flagState.texture.dispose();
  }
  flagState.texture = null;
}

function dispatchFlagTextureChanged() {
  window.dispatchEvent(
    new CustomEvent(VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT),
  );
}

async function reloadFlagTexture() {
  disposeActiveTexture();

  const url = flagState.dataUrl || flagState.presetUrl;

  if (url) {
    try {
      flagState.texture = configureFlagTexture(
        await loader.loadAsync(url),
      );
      flagState.isDefault = false;
    } catch (err) {
      console.warn('[venueHeaderFlagTexture] Failed to load flag from URL:', url, err);
      // Fallback to checkered flag
      flagState.texture = configureFlagTexture(
        await loader.loadAsync(DEFAULT_VENUE_HEADER_FLAG_URL),
      );
      flagState.isDefault = true;
      flagState.fileLabel = DEFAULT_VENUE_HEADER_FLAG_LABEL;
    }
  } else {
    flagState.texture = configureFlagTexture(
      await loader.loadAsync(DEFAULT_VENUE_HEADER_FLAG_URL),
    );
    flagState.isDefault = true;
    flagState.fileLabel = DEFAULT_VENUE_HEADER_FLAG_LABEL;
  }

  return flagState.texture;
}

/**
 * @returns {Promise<THREE.Texture>}
 */
export async function loadVenueHeaderFlagTexture() {
  if (flagState.texture) return flagState.texture;
  return reloadFlagTexture();
}

export function isDefaultVenueHeaderFlag() {
  return flagState.isDefault;
}

/**
 * @returns {{ dataUrl: string | null; presetUrl: string | null; fileLabel: string; isDefault: boolean }}
 */
export function getVenueHeaderFlagTextureState() {
  return {
    dataUrl: flagState.dataUrl,
    presetUrl: flagState.presetUrl,
    fileLabel: flagState.fileLabel,
    isDefault: flagState.isDefault,
  };
}

/**
 * @param {string} url
 * @param {string} fileLabel
 * @param {{ silent?: boolean }} [options]
 */
export async function applyVenueHeaderFlagFromPresetUrl(
  url,
  fileLabel,
  { silent = false } = {},
) {
  flagState.dataUrl = null;
  flagState.presetUrl = url;
  flagState.fileLabel = fileLabel;
  await reloadFlagTexture();
  if (!silent) dispatchFlagTextureChanged();
}

/**
 * @param {string} dataUrl
 * @param {string} fileLabel
 */
export async function applyVenueHeaderFlagTextureFromDataUrl(dataUrl, fileLabel) {
  flagState.dataUrl = dataUrl;
  flagState.presetUrl = null;
  flagState.fileLabel = fileLabel;
  await reloadFlagTexture();
  dispatchFlagTextureChanged();
}

/**
 * @param {string} dataUrl
 * @param {string} [fileLabel]
 */
export async function applyVenueHeaderFlagTextureFromDataUrlSilent(
  dataUrl,
  fileLabel = 'Saved flag',
) {
  flagState.dataUrl = dataUrl;
  flagState.presetUrl = null;
  flagState.fileLabel = fileLabel;
  await reloadFlagTexture();
}

export async function resetVenueHeaderFlagTextureSilent() {
  flagState.dataUrl = null;
  flagState.presetUrl = null;
  flagState.fileLabel = DEFAULT_VENUE_HEADER_FLAG_LABEL;
  await reloadFlagTexture();
}

export async function resetVenueHeaderFlagTexture() {
  await resetVenueHeaderFlagTextureSilent();
  dispatchFlagTextureChanged();
}

/**
 * @param {string} dataUrl
 * @param {string} [fileLabel]
 */
export async function restoreVenueHeaderFlagTextureFromPreset(
  dataUrl,
  fileLabel = 'Saved flag',
) {
  await applyVenueHeaderFlagTextureFromDataUrlSilent(dataUrl, fileLabel);
}
